/* ⚛ REACT */
import { useState, useRef, useEffect } from "react";

/* 📦 LIBS */
import ReCAPTCHA from "react-google-recaptcha";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router";
import { useTranslation, Trans } from "react-i18next";

/* 🧩 COMPONENTS */
import { Input, InputPassword, Label, Button, Modal } from "../../Components";

/* 🔗 SERVICE */
import { authService } from "../../Services/authServices/requests.ts";
import { requestUser } from "../../Services/userServices/request.ts";

/* 🧠 REDUX */
import { store } from "../../Redux/Store";
import { SignIn, changeModal, ChangeCheckedTutorial } from "../../Redux/Actions";

/* ⚙ CONFIG*/
import config from "../../Config/config.json";

/* 📁 ASSETS*/
import { Logo_Mark, Lock, UserIcon } from "../../Assets/icons";
import logoBio from '../../Assets/Images/logoBio.png';

/* 🎨 STYLES */
import { Container, BlueContainer, Title, LoginCotaniner, LoginBox, Image, MobileGradiante, Wellcometext,
    ContainerForm, ImageMobile, CaptchaContainer, PasswordRecovery, DataPrivacy } from "./styles";

/* 🔧 UTILS */
import { isValidField, ValidateField } from "../../util/validate";

const Login = () => {
    document.title = "Augen Login";
    
    const navigate = useNavigate();
    const location = useLocation();
    const { t, i18n } = useTranslation(); 
    
    const userName = useRef('');
    const userPassword = useRef('');
    const recaptchaValue = useRef();
    const recaptchaValueEmail = useRef();
    const userField = useRef();
    const passwordField = useRef();
    
    const emailValue = useRef('');
    const emailField = useRef('');
    
    const [loginStarted, setLoginStarted] = useState(false);

    async function SendLogin(){
        ValidateField(userField.current)
        ValidateField(passwordField.current)
        try {
            userName.current = userField.current.getInputDOMNode().value;
            userPassword.current = passwordField.current.value;
        } catch (error) {
            console.log('Erro ao pegar os valores dos campos: '+error)
        }
        let captchaValue = recaptchaValue.current.getValue();
        if(!captchaValue){
            toast(t("message.login.robot"), {
                position: "top-center",
                type: 'info',
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: false,
                draggable: false,
                progress: undefined,
                theme: config.theme,
            });
        }else if(isValidField(userName.current) && isValidField(userPassword.current)){
            try{
                setLoginStarted(true);
                recaptchaValue.current.reset();
                await authService.SignIn(userName.current, userPassword.current, captchaValue, navigator?.userAgent);
                const newUser = await requestUser.getCurrentUser();

                if (newUser?.user?.lang) {
                    i18n.changeLanguage(newUser.user.lang);
                }

                if(newUser?.data?.message){
                    toast(newUser.data.message, {
                        position: "top-center",
                        type: 'error',
                        autoClose: 3000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: false,
                        draggable: false,
                        progress: undefined,
                        theme: config.theme,
                    });
                }else{
                    store.dispatch(SignIn(newUser));
                    store.dispatch(ChangeCheckedTutorial(newUser.user.checkedTutorial));
                    const from = location.state?.from || "/";
                    setTimeout(() => {
                        navigate(from);
                    }, 20);
                }
            }catch(error) {
                toast(error.message, {
                    position: "top-center",
                    type: 'error',
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: false,
                    draggable: false,
                    progress: undefined,
                    theme: config.theme,
                });
            }finally{
                setLoginStarted(false);
            }
        }else{
            toast(t("message.login.fill"), {
                position: "top-center",
                type: 'error',
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: false,
                draggable: false,
                progress: undefined,
                theme: config.theme,
            });
        }
    }

    async function SendEmail(){
        let captchaValue = recaptchaValueEmail.current.getValue();
        if(!captchaValue){
            toast(t("message.login.robot"), {
                position: "top-center",
                type: 'info',
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: false,
                draggable: false,
                progress: undefined,
                theme: config.theme,
            });
            return;
        }else if(isValidField(emailValue.current)){
            try {
                recaptchaValueEmail.current.reset();
                store.dispatch(changeModal(''))
                const response = await authService.ForgetPassWrod(emailValue.current, captchaValue);
                toast(response.message, {
                    position: "top-center",
                    type: 'success',
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: false,
                    draggable: false,
                    progress: undefined,
                });
            } catch (error) {
                toast(error.message, {
                    position: "top-center",
                    type: 'error',
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: false,
                    draggable: false,
                    progress: undefined,
                });
            }
        }else{
            toast(t("message.save"), {
                position: "top-center",
                type: 'error',
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: false,
                draggable: false,
                progress: undefined,
                theme: config.theme,
            });
        }
    }

    useEffect(() => {
        if(localStorage.getItem('firstAccess') === null){
            store.dispatch(changeModal('EmailLogin'));
            localStorage.setItem('firstAccess', 'true');
        }
    }, []);

    return(
        <>
            <Container>
                <BlueContainer>
                    <Title>
                        <Trans i18nKey="login.title" components={{ br: <br /> }} />
                    </Title>
                </BlueContainer>
                <LoginCotaniner>
                    <MobileGradiante>
                        <LoginBox>
                            <ContainerForm>
                                <Image src={logoBio}/>
                                <Wellcometext>{t("login.welcome-message")}</Wellcometext>
                                <Label text={t("inputs.email-user.label")} aling="flex-start"/>
                                <Input 
                                    id='email' 
                                    name='email' 
                                    type="text" 
                                    innerRef={userField}
                                    invalidMessage = {t("inputs.email-user.invalid-message")} 
                                    value={userName.current}
                                    icon= {UserIcon}
                                    validationFunction = {(text) => isValidField(text)}
                                    onchange={(text) => userName.current = text}> 
                                </Input>

                                <Label text={t("inputs.password.label")} aling="flex-start"/>
                                <InputPassword 
                                    id='password' 
                                    name='password' 
                                    innerRef={passwordField}
                                    invalidMessage = {t("login.password.invalid-message")} 
                                    value={userPassword.current}
                                    icon= {Lock}
                                    validationFunction = {(text) => isValidField(text)}
                                    onchange={(text) => userPassword.current = text}>
                                </InputPassword>


                                <CaptchaContainer>
                                    <ReCAPTCHA ref={recaptchaValue} sitekey={config.Captchakey} theme={config.theme} hl={t("login.ReCAPTCHA")}/>
                                </CaptchaContainer>

                                <DataPrivacy href="https://augenengenharia.com/politica" target="blank">{t("buttons.data-privacy")}</DataPrivacy>
                                
                                <Button text={loginStarted ? t("buttons.entering") : t("buttons.enter")} disabled={loginStarted ? true : false} width={"100%"} onClick={() => SendLogin()}></Button>

                                <PasswordRecovery onClick={()=> store.dispatch(changeModal('ForgetPassword'))}>
                                    <Trans i18nKey="login.forgot-password" components={{ b: <b /> }} />
                                </PasswordRecovery>
                                <ImageMobile src={Logo_Mark}/>
                            </ContainerForm>
                        </LoginBox>
                    </MobileGradiante>
                </LoginCotaniner>
            </Container>

            <Modal title={t("modal.forgot-password.title")} name={'ForgetPassword'} width={'60%'} height={'60%'}>
                <Label text={t("inputs.email-user.label")} aling="flex-start"/>
                <Input 
                    id='email' 
                    name='email' 
                    innerRef={emailField}
                    placeholder= {t("inputs.email-user.placeholder")}
                    invalidMessage = {t("inputs.email-user.invalid-message")}
                    validationFunction = {(text) => isValidField(text)}
                    onchange={(text) => emailValue.current = text}>
                </Input>

                <CaptchaContainer>
                    <ReCAPTCHA ref={recaptchaValueEmail} sitekey={config.Captchakey} theme={config.theme} hl={t("login.ReCAPTCHA")}/>
                </CaptchaContainer>

                <Button text={t("buttons.submit")} width={'120px'} onClick={() => SendEmail()}/>
            </Modal>

            <Modal title={t("modal.email-login.title")} name={'EmailLogin'} width={'60%'} height={'60%'}>
                <Wellcometext>{t("modal.email-login.content")}</Wellcometext>
            </Modal>
        </>
    )
}

export default Login;